// frontend/src/pages/ArticleView.js

import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import CommentSection from '../components/UI/CommentSection';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

function getFullMediaUrl(path) {
  if (!path) return '';
  if (path.startsWith('/uploads')) {
    return `https://entecity.ch${path}`;
  }
  return path;
}

function transformLinks(html = '') {
  return html.replace(/<a\s+[^>]*href\s*=\s*"([^"]+)"[^>]*>/gi, (originalTag, hrefValue) => {
    let href = hrefValue.trim();
    const isLocalLink = href.startsWith('/') || href.startsWith('#');

    if (!isLocalLink) {
      if (!/^https?:\/\//i.test(href)) {
        href = 'https://' + href;
      }
      const newTag = originalTag
        .replace(hrefValue, href)
        .replace(/>/, ' target="_blank" rel="noreferrer noopener">');
      return newTag;
    }
    return originalTag;
  });
}

const ArticleView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchArticle = async () => {
    try {
      const { data } = await axiosInstance.get(`/articles/${slug}`);
      setArticle(data);
      setError('');
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setError('Artikel nicht gefunden');
      } else {
        setError('Fehler: Artikel konnte nicht geladen werden.');
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchArticle();
    // eslint-disable-next-line
  }, [slug]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="loader" />
      </div>
    );
  }

  if (error) {
    return (
      <motion.div
        className="text-center py-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <h1 className="text-red-500 text-2xl mb-4">{error}</h1>
        {error === 'Artikel nicht gefunden' && (
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-orange-600"
          >
            Zur Startseite
          </button>
        )}
      </motion.div>
    );
  }

  if (!article) return null;

  const allMedia = [
    ...(article.media?.images || []),
    ...(article.media?.videos || [])
  ];

  const transformedContent = transformLinks(article.content);

  return (
    <motion.div
      className="py-8 max-w-4xl mx-auto px-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h1 className="text-4xl md:text-5xl font-heading text-primary mb-4 break-words">
        {article.title}
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        {article.publishDate
          ? new Date(article.publishDate).toLocaleDateString()
          : 'Entwurf'}{' '}
        | {article.category}
      </p>

      {/* Media Carousel */}
      {allMedia.length > 0 && (
        <div className="mb-6 w-full">
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 4000 }}
            loop
            style={{ width: '100%', height: 'auto' }}
            className="relative"
          >
            {allMedia.map((m, idx) => {
              const fullUrl = getFullMediaUrl(m);

              if (m.match(/\.(mp4|mov|avi)$/i)) {
                return (
                  <SwiperSlide key={idx}>
                    <div className="flex justify-center items-center w-full h-96 bg-black">
                      <video
                        src={fullUrl}
                        controls
                        className="max-h-96 w-auto object-cover"
                      />
                    </div>
                  </SwiperSlide>
                );
              } else {
                return (
                  <SwiperSlide key={idx}>
                    <img
                      src={fullUrl}
                      alt={`media-${idx}`}
                      className="w-full h-96 object-cover"
                    />
                  </SwiperSlide>
                );
              }
            })}
          </Swiper>
        </div>
      )}

      <div
        className="prose max-w-none mb-8 break-words"
        dangerouslySetInnerHTML={{ __html: transformedContent }}
      />

      <CommentSection articleId={article._id} />
    </motion.div>
  );
};

export default ArticleView;
